import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import TableSortLabel from '@mui/material/TableSortLabel';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import InputAdornment from '@mui/material/InputAdornment';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import { saveFile } from '../../native';
import {
  INDIAN_STATES,
  AdminClaimRow,
  AdminOfferRow,
  AdminPartner,
  AdminPartnerInput,
  AuditLogEntry,
  createAdminPartner,
  deleteAdminPartner,
  getAdminClaims,
  getAdminOffers,
  getAdminPartners,
  getAuditLogs,
  getPartnerApplications,
  moderateOffer,
  PartnerApplication,
  rejectPartnerApplication,
  updateAdminPartner,
  venueImageUrl,
  verifyVenue,
  verifyVenueLicence,
  getOfferHistory,
  OfferRevision,
  OfferSnapshot
} from '../../api';
import { categoryLabel, formatDays, formatDiscount } from '../../features';
import { AdminPoolPartyRow, getAdminPoolParties, ModerationAction, moderatePoolParty, partyPrice, partyWhen } from '../../features/poolParties/api';
import { AdminEventRow, eventWhen, getAdminEvents, moderateEvent } from '../../features/events/api';

const GOLD = '#c9a227';
const goldButton = { background: 'linear-gradient(135deg, #c9a227, #dbb42c)', color: '#000', fontWeight: 700 };
const STATUS_COLORS: Record<string, string> = {
  Active: '#22c55e', Redeemed: '#22c55e', Approved: '#22c55e', Draft: '#6b7280', Claimed: '#6b7280', Completed: '#6b7280', Paused: '#f59e0b', SoldOut: '#f59e0b',
  Scheduled: '#22d3ee', Confirmed: '#22d3ee', Pending: '#a78bfa', Expired: '#ef4444', Rejected: '#ef4444', Suspended: '#ef4444', Cancelled: '#ef4444'
};

const errorText = (err: unknown, fallback: string) =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? fallback;

const fmtDate = (iso?: string) => (iso ? new Date(iso.length === 10 ? `${iso}T00:00:00` : iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '–');
const fmtDateTime = (iso?: string) => (iso ? new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '–');

function StatusChip({ status }: { status: string }) {
  const bg = STATUS_COLORS[status] ?? '#6b7280';
  return <Chip size="small" label={status === 'SoldOut' ? 'Sold Out' : status} sx={{ background: bg, color: ['#22c55e', '#f59e0b', '#22d3ee', '#a78bfa'].includes(bg) ? '#000' : '#fff', fontWeight: 700 }} />;
}

// ---------- Listing review (offers and pool parties) ----------

type ReviewStatus = 'Pending' | 'Approved' | 'Rejected' | 'Suspended';
interface ReviewTarget {
  kind: 'offer' | 'pool party' | 'event';
  id: number;
  title: string;
  venueName: string;
  action: ModerationAction;
  // Approving a suspended listing reinstates it.
  reinstate?: boolean;
  // What the partner wrote, so the admin can judge it without leaving the console.
  details: [string, string | null | undefined][];
}

const ACTION_LABEL: Record<ModerationAction, string> = { approve: 'Approve', reject: 'Reject', suspend: 'Suspend' };

// Buttons for the review actions that make sense from the listing's current review state.
function ReviewButtons({ status, onAction }: { status: ReviewStatus; onAction: (a: ModerationAction) => void }) {
  const actions: ModerationAction[] = status === 'Pending' ? ['approve', 'reject'] : status === 'Approved' ? ['suspend'] : ['approve'];
  return (
    <>
      {actions.map((a) => (
        <Button key={a} size="small" variant={a === 'approve' ? 'contained' : 'text'} color={a === 'approve' ? 'inherit' : 'error'}
          sx={a === 'approve' ? { ...goldButton, mr: 0.5 } : undefined} onClick={() => onAction(a)}>
          {a === 'approve' && status === 'Suspended' ? 'Reinstate' : ACTION_LABEL[a]}
        </Button>
      ))}
    </>
  );
}

function ReviewDialog({ target, onClose, onDone }: { target: ReviewTarget | null; onClose: () => void; onDone: (message: string) => void }) {
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setNote(''); setError(null); }, [target]);
  if (!target) return null;
  const needsReason = target.action !== 'approve';
  // Offers need no approval, so for them "approve" only reinstates a suspended offer.
  const reinstate = !!target.reinstate && target.action === 'approve';
  const label = reinstate ? 'Reinstate' : ACTION_LABEL[target.action];

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const reason = note.trim() || undefined;
      const result = target.kind === 'offer'
        ? await moderateOffer(target.id, target.action, reason)
        : target.kind === 'event'
          ? await moderateEvent(target.id, target.action, reason)
          : await moderatePoolParty(target.id, target.action, reason);
      onDone(result.message);
    } catch (err) {
      setError(errorText(err, 'Could not save the review.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{label} {target.kind}: {target.title}</DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 1.5 }}>
        {error && <Alert severity="error">{error}</Alert>}
        <Typography fontSize={13} color="text.secondary">{target.venueName}</Typography>
        {target.details.filter(([, v]) => v).map(([label, value]) => (
          <Box key={label}><Typography fontSize={12} fontWeight={700} color="text.secondary">{label}</Typography><Typography fontSize={14} sx={{ whiteSpace: 'pre-line' }}>{value}</Typography></Box>
        ))}
        <Alert severity={target.action === 'approve' ? 'info' : 'warning'}>
          {target.action === 'approve'
            ? reinstate ? 'Reinstating makes the offer visible to customers again (within its dates).' : 'Approving makes it visible to customers. Check it is lawful, accurate and doesn\'t imply alcohol is sold or delivered by Cheers2Night.'
            : `The ${target.kind} is hidden from customers and the partner is emailed this reason.`}
        </Alert>
        {needsReason && (
          <TextField label="Reason (sent to the partner)" required multiline minRows={2} value={note} onChange={(e) => setNote(e.target.value)} inputProps={{ maxLength: 500 }} autoFocus />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" color={needsReason ? 'error' : 'inherit'} sx={needsReason ? undefined : goldButton} disabled={busy || (needsReason && !note.trim())} onClick={submit}>
          {busy ? 'Saving...' : label}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// CSV of whatever the admin is looking at (after search/sort), opened by Excel/Sheets.
function downloadCsv(filename: string, header: string[], rows: (string | number | boolean | undefined | null)[][]) {
  const cell = (v: unknown) => {
    const s = v === undefined || v === null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [header, ...rows].map((r) => r.map(cell).join(',')).join('\r\n');
  saveFile(filename, new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' })).catch(() => alert('Could not export the file.'));
}

type Order = 'asc' | 'desc';
function sortRows<T>(rows: T[], key: keyof T, order: Order): T[] {
  return [...rows].sort((a, b) => {
    const x = a[key] as unknown;
    const y = b[key] as unknown;
    const cmp = typeof x === 'number' && typeof y === 'number' ? x - y : String(x ?? '').localeCompare(String(y ?? ''), undefined, { numeric: true });
    return order === 'asc' ? cmp : -cmp;
  });
}

function useTableState<T>(rows: T[], matches: (row: T, q: string) => boolean, defaultSort: keyof T, defaultOrder: Order = 'asc') {
  const [query, setQuery] = useState('');
  const [sortKey, setSortKey] = useState<keyof T>(defaultSort);
  const [order, setOrder] = useState<Order>(defaultOrder);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return sortRows(q ? rows.filter((r) => matches(r, q)) : rows, sortKey, order);
  }, [rows, query, sortKey, order, matches]);
  useEffect(() => setPage(0), [query, perPage]);
  const sortBy = (key: keyof T) => {
    setOrder(sortKey === key && order === 'asc' ? 'desc' : 'asc');
    setSortKey(key);
  };
  const pageRows = filtered.slice(page * perPage, page * perPage + perPage);
  return { query, setQuery, sortKey, order, sortBy, page, setPage, perPage, setPerPage, filtered, pageRows };
}

function Toolbar({ query, setQuery, placeholder, onExport, onRefresh, children }: {
  query: string; setQuery: (q: string) => void; placeholder: string; onExport: () => void; onRefresh: () => void; children?: React.ReactNode;
}) {
  return (
    <Box display="flex" gap={1.5} flexWrap="wrap" alignItems="center" mb={2}>
      <TextField
        size="small"
        placeholder={placeholder}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        sx={{ flex: '1 1 260px' }}
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
      />
      <Tooltip title="Reload"><IconButton onClick={onRefresh} aria-label="Reload"><RefreshIcon /></IconButton></Tooltip>
      <Button variant="outlined" startIcon={<FileDownloadOutlinedIcon />} onClick={onExport} sx={{ borderColor: GOLD, color: GOLD }}>Export CSV</Button>
      {children}
    </Box>
  );
}

function SortHead<T>({ label, field, state, align }: { label: string; field: keyof T; state: { sortKey: keyof T; order: Order; sortBy: (k: keyof T) => void }; align?: 'right' }) {
  return (
    <TableCell align={align} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
      <TableSortLabel active={state.sortKey === field} direction={state.sortKey === field ? state.order : 'asc'} onClick={() => state.sortBy(field)}>
        {label}
      </TableSortLabel>
    </TableCell>
  );
}

function Pager({ count, page, perPage, setPage, setPerPage }: { count: number; page: number; perPage: number; setPage: (p: number) => void; setPerPage: (n: number) => void }) {
  return (
    <TablePagination
      component="div"
      count={count}
      page={Math.min(page, Math.max(0, Math.ceil(count / perPage) - 1))}
      rowsPerPage={perPage}
      rowsPerPageOptions={[10, 25, 50, 100]}
      onPageChange={(_, p) => setPage(p)}
      onRowsPerPageChange={(e) => setPerPage(Number(e.target.value))}
    />
  );
}

// ---------- Partner add / edit ----------

interface PartnerForm {
  name: string;
  address: string;
  mobile1: string;
  mobile2: string;
  latitude: string;
  longitude: string;
  state: string;
  district: string;
  ownerEmail: string;
  password: string;
}

const EMPTY_FORM: PartnerForm = { name: '', address: '', mobile1: '', mobile2: '', latitude: '', longitude: '', state: '', district: '', ownerEmail: '', password: '' };

// An application's free-text state, matched to the list (case and spacing ignored).
const matchState = (s?: string | null) => INDIAN_STATES.find((x) => x.toLowerCase() === (s ?? '').trim().replace(/\s+/g, ' ').toLowerCase()) ?? '';

// Accepts "18.5362, 73.8940" pasted from Google Maps into the latitude box.
const splitCoordinates = (value: string): [string, string] | null => {
  const m = value.match(/^\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*$/);
  return m ? [m[1], m[2]] : null;
};

function PartnerDialog({ partner, application, open, onClose, onSaved }: {
  partner: AdminPartner | null; application?: PartnerApplication | null; open: boolean; onClose: () => void; onSaved: (message: string, generatedPassword?: string, email?: string) => void;
}) {
  const isEdit = partner !== null;
  const [form, setForm] = useState<PartnerForm>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setForm(partner ? {
      name: partner.name, address: partner.address, mobile1: partner.mobile1 ?? '', mobile2: partner.mobile2 ?? '',
      latitude: String(partner.latitude), longitude: String(partner.longitude), state: partner.state ?? '', district: partner.district ?? '',
      ownerEmail: partner.ownerEmail ?? '', password: ''
    } : application ? {
      ...EMPTY_FORM,
      state: matchState(application.state),
      district: application.city,
      name: application.businessName,
      address: [application.city, application.state, application.pinCode].filter(Boolean).join(', '),
      mobile1: application.mobile,
      ownerEmail: application.email
    } : EMPTY_FORM);
  }, [open, partner, application]);

  const set = (key: keyof PartnerForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const value = e.target.value;
    const pair = key === 'latitude' ? splitCoordinates(value) : null;
    setForm((f) => (pair ? { ...f, latitude: pair[0], longitude: pair[1] } : { ...f, [key]: value }));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const lat = Number(form.latitude);
    const lng = Number(form.longitude);
    if (form.latitude.trim() === '' || form.longitude.trim() === '' || Number.isNaN(lat) || Number.isNaN(lng)) {
      setError('Enter the latitude and longitude as numbers, e.g. 18.5362 and 73.8940.');
      return;
    }
    const input: AdminPartnerInput = {
      name: form.name, address: form.address, mobile1: form.mobile1, mobile2: form.mobile2 || null,
      latitude: lat, longitude: lng, state: form.state || null, district: form.state ? form.district.trim() || null : null,
      ownerEmail: form.ownerEmail || null, password: form.password || null,
      applicationId: !isEdit && application ? application.id : null
    };
    setSaving(true);
    try {
      const result = isEdit ? await updateAdminPartner(partner!.restoId, input) : await createAdminPartner(input);
      onSaved(result.message, result.generatedPassword, result.partner?.ownerEmail);
    } catch (err) {
      setError(errorText(err, 'Could not save the partner.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm" component="form" onSubmit={handleSave}>
      <DialogTitle>{isEdit ? `Edit ${partner!.name}` : application ? `Approve ${application.businessName}` : 'Add Partner'}</DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 2, pt: '8px !important' }}>
        {error && <Alert severity="error">{error}</Alert>}
        {application && !isEdit && (
          <Alert severity="info">Check the details, complete the full address and map location, then save. The owner is emailed their login at {application.email}.</Alert>
        )}
        <Typography variant="overline" color="text.secondary">Venue</Typography>
        <TextField label="Venue name" required value={form.name} onChange={set('name')} inputProps={{ maxLength: 120 }} />
        <TextField label="Address" required multiline minRows={2} value={form.address} onChange={set('address')} inputProps={{ maxLength: 300 }} />
        <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={2}>
          <TextField label="Mobile" required type="tel" value={form.mobile1} onChange={set('mobile1')} />
          <TextField label="Alternate mobile" type="tel" value={form.mobile2} onChange={set('mobile2')} />
          <TextField label="Latitude" required value={form.latitude} onChange={set('latitude')} placeholder="18.5362" />
          <TextField label="Longitude" required value={form.longitude} onChange={set('longitude')} placeholder="73.8940" />
        </Box>
        <Typography variant="caption" color="text.secondary" mt={-1}>
          In Google Maps, right-click the venue and click the numbers at the top to copy them; paste into Latitude and both boxes fill in.
        </Typography>
        <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={2}>
          <TextField select label="State" value={form.state} onChange={set('state')} SelectProps={{ native: true }} InputLabelProps={{ shrink: true }}>
            <option value="">Not set</option>
            {INDIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
          </TextField>
          <TextField label="District / City" value={form.district} onChange={set('district')} disabled={!form.state} inputProps={{ maxLength: 60 }} placeholder="Pune" />
        </Box>
        <Typography variant="caption" color="text.secondary" mt={-1}>
          Used by the State / District filters on Explore, Offers and Events. Use the same district spelling for every venue there.
        </Typography>
        <Typography variant="overline" color="text.secondary">Owner login</Typography>
        <TextField label="Owner email" type="email" required={!isEdit} value={form.ownerEmail} onChange={set('ownerEmail')} helperText={isEdit ? 'Changing it moves the partner\'s login to the new email.' : 'The partner logs in with this email.'} />
        <TextField
          label={isEdit ? 'New password (optional)' : 'Password (optional)'}
          type="text"
          value={form.password}
          onChange={set('password')}
          autoComplete="new-password"
          helperText={isEdit ? 'Leave empty to keep the current password.' : 'Leave empty to generate one; it\'s shown once after saving.'}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button type="submit" variant="contained" disabled={saving} sx={goldButton}>{saving ? 'Saving...' : isEdit ? 'Save Changes' : application ? 'Approve & Create Partner' : 'Add Partner'}</Button>
      </DialogActions>
    </Dialog>
  );
}

function DeleteDialog({ partner, onClose, onDeleted }: { partner: AdminPartner | null; onClose: () => void; onDeleted: (message: string) => void }) {
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setConfirm(''); setError(null); }, [partner]);
  if (!partner) return null;

  const handleDelete = async () => {
    setBusy(true);
    try {
      await deleteAdminPartner(partner.restoId);
      onDeleted(`Partner "${partner.name}" deleted.`);
    } catch (err) {
      setError(errorText(err, 'Could not delete the partner.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>Delete {partner.name}?</DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 2 }}>
        {error && <Alert severity="error">{error}</Alert>}
        <Alert severity="warning">
          This permanently deletes the venue{partner.ownerEmail ? ` and ${partner.ownerEmail}'s login` : ''}, its photos,
          {' '}{partner.totalOffers} offer(s) and {partner.totalClaims} customer claim(s). It can't be undone.
        </Alert>
        <TextField label={`Type "${partner.name}" to confirm`} value={confirm} onChange={(e) => setConfirm(e.target.value)} autoFocus />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button color="error" variant="contained" disabled={busy || confirm.trim() !== partner.name.trim()} onClick={handleDelete}>
          {busy ? 'Deleting...' : 'Delete Partner'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------- Tables ----------

const partnerMatches = (p: AdminPartner, q: string) =>
  `${p.name} ${p.address} ${p.mobile1 ?? ''} ${p.mobile2 ?? ''} ${p.ownerEmail ?? ''}`.toLowerCase().includes(q);
const offerMatches = (o: AdminOfferRow, q: string) => `${o.venueName} ${o.title} ${o.benefit ?? ''} ${o.status}`.toLowerCase().includes(q);
const claimMatches = (c: AdminClaimRow, q: string) => `${c.venueName} ${c.offerTitle} ${c.mobile} ${c.status}`.toLowerCase().includes(q);

// A venue's liquor licence as entered by the partner, and the admin's verification of it against the
// excise records. Alcohol-related offers are shown only while it's verified and unexpired.
// Whether an admin has checked the venue's name, address, phone and map location. Only verified
// venues' offers are shown to customers.
function VenueVerifiedCell({ partner: p, onChanged }: { partner: AdminPartner; onChanged: (text: string, ok: boolean) => void }) {
  const [busy, setBusy] = useState(false);
  const verified = !!p.verifiedAt;
  const toggle = async () => {
    if (!verified && !window.confirm(`Mark ${p.name}'s details (name, address, phone, map location) as checked? Its approved offers will then be shown.`)) return;
    if (verified && !window.confirm(`Remove verification from ${p.name}? All its offers will be hidden.`)) return;
    setBusy(true);
    try {
      onChanged((await verifyVenue(p.restoId, !verified)).message, true);
    } catch (err) {
      onChanged(errorText(err, 'Could not update the venue.'), false);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Box>
      <Chip size="small" label={verified ? 'Verified' : 'Not verified'} color={verified ? 'success' : 'warning'} variant="outlined" />
      {verified && <Typography fontSize={11} color="text.secondary">{fmtDate(p.verifiedAt ?? undefined)}{p.verifiedBy ? ` · ${p.verifiedBy}` : ''}</Typography>}
      <Box><Button size="small" disabled={busy} onClick={toggle}>{verified ? 'Unverify' : 'Verify'}</Button></Box>
    </Box>
  );
}

function LicenceCell({ partner: p, onChanged }: { partner: AdminPartner; onChanged: (text: string, ok: boolean) => void }) {
  const [busy, setBusy] = useState(false);
  if (!p.liquorLicenceNumber) return <Typography fontSize={12} color="text.secondary">None</Typography>;
  const expired = !!p.liquorLicenceExpiry && new Date(`${p.liquorLicenceExpiry}T23:59:59`) < new Date();
  const verified = !!p.liquorLicenceVerifiedAt && !expired;

  const toggle = async () => {
    if (!verified && !window.confirm(`Mark licence ${p.liquorLicenceNumber} of ${p.name} as verified? Only do this after checking it against the state excise records.`)) return;
    setBusy(true);
    try {
      await verifyVenueLicence(p.restoId, !verified);
      onChanged(`${p.name}: licence ${verified ? 'verification removed' : 'verified'}.`, true);
    } catch (err) {
      onChanged(errorText(err, 'Could not update the licence.'), false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box>
      <Typography fontSize={13} sx={{ fontFamily: 'monospace' }}>{p.liquorLicenceNumber}</Typography>
      <Typography fontSize={11} color={expired ? 'error.light' : 'text.secondary'}>Valid until {fmtDate(p.liquorLicenceExpiry ?? undefined)}{expired ? ' (expired)' : ''}</Typography>
      <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
        <Chip size="small" label={verified ? 'Verified' : expired ? 'Expired' : 'Not verified'} color={verified ? 'success' : 'warning'} variant="outlined" />
        {!expired && <Button size="small" disabled={busy} onClick={toggle}>{verified ? 'Unverify' : 'Verify'}</Button>}
      </Box>
    </Box>
  );
}

function PartnersTable({ rows, reload, notify }: { rows: AdminPartner[]; reload: () => void; notify: (n: { severity: 'success' | 'error'; text: string; password?: string; email?: string }) => void }) {
  const navigate = useNavigate();
  const t = useTableState(rows, partnerMatches, 'name');
  const [editing, setEditing] = useState<AdminPartner | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState<AdminPartner | null>(null);

  const exportCsv = () => downloadCsv('cheers2night-partners.csv',
    ['ID', 'Venue', 'Address', 'Mobile', 'Alternate mobile', 'Latitude', 'Longitude', 'Owner email', 'Has login', 'Live offers', 'Total offers', 'Claims', 'Redeemed'],
    t.filtered.map((p) => [p.restoId, p.name, p.address, p.mobile1, p.mobile2, p.latitude, p.longitude, p.ownerEmail, p.hasLogin, p.liveOffers, p.totalOffers, p.totalClaims, p.redeemed]));

  return (
    <>
      <Toolbar query={t.query} setQuery={t.setQuery} placeholder="Search venue, address, mobile or owner email" onExport={exportCsv} onRefresh={reload}>
        <Button variant="contained" startIcon={<AddIcon />} sx={goldButton} onClick={() => { setEditing(null); setDialogOpen(true); }}>Add Partner</Button>
      </Toolbar>
      <TableContainer sx={{ border: '1px solid #2a2f38', borderRadius: 2, colorScheme: 'dark' }}>
        <Table size="small" sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow>
              <SortHead label="ID" field="restoId" state={t} />
              <SortHead label="Venue" field="name" state={t} />
              <SortHead label="Mobile" field="mobile1" state={t} />
              <SortHead label="Owner email" field="ownerEmail" state={t} />
              <TableCell sx={{ fontWeight: 700 }}>Location</TableCell>
              <SortHead label="Offers (live/total)" field="liveOffers" state={t} align="right" />
              <SortHead label="Venue details" field="verifiedAt" state={t} />
              <SortHead label="Liquor licence" field="liquorLicenceNumber" state={t} />
              <SortHead label="Claims" field="totalClaims" state={t} align="right" />
              <SortHead label="Redeemed" field="redeemed" state={t} align="right" />
              <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {t.pageRows.length === 0 && (
              <TableRow><TableCell colSpan={11} align="center" sx={{ py: 4, color: 'text.secondary' }}>{rows.length === 0 ? 'No partners yet. Add one to get started.' : 'No partners match your search.'}</TableCell></TableRow>
            )}
            {t.pageRows.map((p) => (
              <TableRow key={p.restoId} hover>
                <TableCell>{p.restoId}</TableCell>
                <TableCell sx={{ minWidth: 220 }}>
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Box sx={{ width: 40, height: 40, borderRadius: 1, overflow: 'hidden', flexShrink: 0, background: '#1a1a1a' }}>
                      {venueImageUrl(p.coverUrl) && <img src={venueImageUrl(p.coverUrl)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                    </Box>
                    <Box minWidth={0}>
                      <Typography fontWeight={700} fontSize={14}>{p.name}</Typography>
                      <Typography fontSize={12} color="text.secondary" sx={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={p.address}>{p.address}</Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{p.mobile1 ?? '–'}{p.mobile2 && <Typography fontSize={12} color="text.secondary">{p.mobile2}</Typography>}</TableCell>
                <TableCell>
                  {p.ownerEmail ?? <Typography component="span" fontSize={13} color="text.secondary">No owner</Typography>}
                  {p.ownerEmail && !p.hasLogin && <Chip size="small" label="No login" color="warning" sx={{ ml: 1 }} />}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <a href={`https://www.google.com/maps?q=${p.latitude},${p.longitude}`} target="_blank" rel="noreferrer" style={{ color: GOLD }}>
                    {p.latitude.toFixed(4)}, {p.longitude.toFixed(4)}
                  </a>
                </TableCell>
                <TableCell align="right">{p.liveOffers} / {p.totalOffers}</TableCell>
                <TableCell sx={{ minWidth: 130 }}><VenueVerifiedCell partner={p} onChanged={(text, ok) => { notify({ severity: ok ? 'success' : 'error', text }); if (ok) reload(); }} /></TableCell>
                <TableCell sx={{ minWidth: 170 }}><LicenceCell partner={p} onChanged={(text, ok) => { notify({ severity: ok ? 'success' : 'error', text }); if (ok) reload(); }} /></TableCell>
                <TableCell align="right">{p.totalClaims}</TableCell>
                <TableCell align="right">{p.redeemed}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  <Tooltip title="Open this venue's partner portal"><IconButton size="small" onClick={() => navigate(`/partner-account?restoId=${p.restoId}`)} aria-label={`Manage ${p.name}`}><OpenInNewIcon fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Edit"><IconButton size="small" onClick={() => { setEditing(p); setDialogOpen(true); }} aria-label={`Edit ${p.name}`}><EditOutlinedIcon fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Delete"><IconButton size="small" onClick={() => setDeleting(p)} aria-label={`Delete ${p.name}`}><DeleteOutlineIcon fontSize="small" color="error" /></IconButton></Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Pager count={t.filtered.length} page={t.page} perPage={t.perPage} setPage={t.setPage} setPerPage={t.setPerPage} />

      <PartnerDialog
        open={dialogOpen}
        partner={editing}
        onClose={() => setDialogOpen(false)}
        onSaved={(message, password, email) => { setDialogOpen(false); notify({ severity: 'success', text: message, password, email }); reload(); }}
      />
      <DeleteDialog
        partner={deleting}
        onClose={() => setDeleting(null)}
        onDeleted={(message) => { setDeleting(null); notify({ severity: 'success', text: message }); reload(); }}
      />
    </>
  );
}

type Notify = (n: { severity: 'success' | 'error'; text: string; password?: string; email?: string }) => void;

// Field labels for the history dialog's "what changed" lines.
const SNAPSHOT_FIELDS: [keyof OfferSnapshot, string][] = [
  ['title', 'Title'], ['category', 'Category'], ['coversAlcohol', 'Covers alcohol'], ['discountType', 'Discount type'], ['discountValue', 'Discount'],
  ['benefit', 'Headline benefit'], ['description', 'Description'], ['terms', 'Terms'], ['minimumSpend', 'Minimum bill'],
  ['startDate', 'Start date'], ['endDate', 'End date'], ['startTime', 'Start time'], ['endTime', 'End time'], ['activeDays', 'Days'],
  ['isPublished', 'Published'], ['isActive', 'Active']
];

const show = (v: unknown) => (v === null || v === undefined || v === '' ? '–' : String(v));

// Every version of an offer and every review decision, oldest first, with what changed each time.
function OfferHistoryDialog({ offer, onClose }: { offer: AdminOfferRow | null; onClose: () => void }) {
  const [history, setHistory] = useState<OfferRevision[] | null>(null);
  useEffect(() => {
    setHistory(null);
    if (offer) getOfferHistory(offer.offerId).then(setHistory).catch(() => setHistory([]));
  }, [offer]);
  if (!offer) return null;
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>History: {offer.title}</DialogTitle>
      <DialogContent>
        {history === null ? <Typography color="text.secondary">Loading...</Typography>
          : history.length === 0 ? <Typography color="text.secondary">No recorded changes yet (offers created before history was kept start with their next change).</Typography>
            : history.map((h, i) => {
              const before = i > 0 ? history[i - 1].snapshot : null;
              const changes = before ? SNAPSHOT_FIELDS.filter(([k]) => show(before[k]) !== show(h.snapshot[k])) : [];
              return (
                <Box key={h.id} sx={{ borderLeft: `3px solid ${['approved'].includes(h.action) ? '#22c55e' : ['rejected', 'suspended'].includes(h.action) ? '#ef4444' : GOLD}`, pl: 1.5, mb: 2 }}>
                  <Typography fontSize={13}><b style={{ textTransform: 'capitalize' }}>{h.action}</b> by {h.actor} · {fmtDateTime(h.createdAt)}</Typography>
                  {h.note && <Typography fontSize={12} color="text.secondary">Reason: {h.note}</Typography>}
                  {i === 0 && <Typography fontSize={12} color="text.secondary">{h.snapshot.title} · {categoryLabel(h.snapshot.category)} · {formatDiscount(h.snapshot.discountType, h.snapshot.discountValue) ?? h.snapshot.benefit}</Typography>}
                  {changes.map(([k, label]) => (
                    <Typography key={k} fontSize={12} color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                      {label}: <s>{show(before![k])}</s> → {show(h.snapshot[k])}
                    </Typography>
                  ))}
                  {h.snapshot.partnerConfirmedAt && h.snapshot.partnerConfirmedAt !== before?.partnerConfirmedAt && (
                    <Typography fontSize={12} color="text.secondary">Accuracy confirmed by {h.snapshot.partnerConfirmedBy}</Typography>
                  )}
                </Box>
              );
            })}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}

function OffersTable({ rows, reload, notify }: { rows: AdminOfferRow[]; reload: () => void; notify: Notify }) {
  const navigate = useNavigate();
  const t = useTableState(rows, offerMatches, 'updatedAt', 'desc');
  const [reviewing, setReviewing] = useState<ReviewTarget | null>(null);
  const [historyOf, setHistoryOf] = useState<AdminOfferRow | null>(null);
  const exportCsv = () => downloadCsv('cheers2night-offers.csv',
    ['Offer ID', 'Venue ID', 'Venue', 'Offer', 'Category', 'Discount', 'Benefit', 'Days', 'Status', 'Review', 'Review note', 'Confirmed by', 'Venue verified', 'Hidden because', 'Start', 'End', 'Views', 'Updated'],
    t.filtered.map((o) => [o.offerId, o.restoId, o.venueName, o.title, categoryLabel(o.category), formatDiscount(o.discountType, o.discountValue), o.benefit, formatDays(o.activeDays),
      o.status, o.moderationStatus, o.moderationNote, o.partnerConfirmedBy, o.venueVerified, o.hiddenReason, o.startDate, o.endDate, o.views, o.updatedAt]));
  const review = (o: AdminOfferRow, action: ModerationAction) => setReviewing({
    kind: 'offer', id: o.offerId, reinstate: o.moderationStatus === 'Suspended', title: o.title, venueName: o.venueName, action,
    details: [
      ['Category', `${categoryLabel(o.category)}${o.category === 'DiningBill' ? (o.coversAlcohol === false ? ' (excludes alcohol)' : ' (covers alcohol)') : ''}`],
      ['Discount', formatDiscount(o.discountType, o.discountValue) ?? o.benefit], ['Headline', o.benefit], ['Description', o.description], ['Terms', o.terms],
      ['Minimum bill', o.minimumSpend ? `₹${o.minimumSpend}` : null],
      ['When', `${fmtDate(o.startDate)} – ${fmtDate(o.endDate)} · ${formatDays(o.activeDays)}${o.startTime ? ` · ${o.startTime.slice(0, 5)}–${o.endTime?.slice(0, 5) ?? ''}` : ''}`],
      ['Partner confirmation', o.partnerConfirmedAt ? `Confirmed accurate and lawful by ${o.partnerConfirmedBy} on ${fmtDateTime(o.partnerConfirmedAt)}` : 'Not confirmed'],
      ['Venue', o.venueVerified ? 'Details verified' : 'Venue details NOT verified (its offers stay hidden)'],
      ['Alcohol', o.isAlcoholRelated ? `Alcohol-related; liquor licence ${o.venueLicenceValid ? 'verified' : 'NOT verified'}` : null],
      ['Previous review note', o.moderationNote]
    ]
  });
  return (
    <>
      <Toolbar query={t.query} setQuery={t.setQuery} placeholder="Search venue, offer or status" onExport={exportCsv} onRefresh={reload} />
      <TableContainer sx={{ border: '1px solid #2a2f38', borderRadius: 2, colorScheme: 'dark' }}>
        <Table size="small" sx={{ minWidth: 1000 }}>
          <TableHead>
            <TableRow>
              <SortHead label="Venue" field="venueName" state={t} />
              <SortHead label="Offer" field="title" state={t} />
              <SortHead label="Status" field="status" state={t} />
              <SortHead label="Dates" field="startDate" state={t} />
              <SortHead label="Views" field="views" state={t} align="right" />
              <SortHead label="Claims" field="claims" state={t} align="right" />
              <SortHead label="Redeemed" field="redeemed" state={t} align="right" />
              <SortHead label="Updated" field="updatedAt" state={t} />
              <SortHead label="Actions" field="moderationStatus" state={t} align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {t.pageRows.length === 0 && <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>No offers found.</TableCell></TableRow>}
            {t.pageRows.map((o) => (
              <TableRow key={o.offerId} hover>
                <TableCell>{o.venueName}</TableCell>
                <TableCell sx={{ minWidth: 200 }}>
                  <Typography fontWeight={700} fontSize={14}>{o.title}{o.isFeatured && <Chip size="small" label="Featured" sx={{ ml: 1, background: GOLD, color: '#000', fontWeight: 700 }} />}</Typography>
                  <Typography fontSize={12} color="text.secondary">{[formatDiscount(o.discountType, o.discountValue), o.benefit].filter(Boolean).join(' · ')}</Typography>
                  {o.moderationNote && <Typography fontSize={12} color="error.light">Note: {o.moderationNote}</Typography>}
                  {o.hiddenReason && <Typography fontSize={12} color="warning.light">Hidden: {o.hiddenReason}</Typography>}
                </TableCell>
                <TableCell>
                  <StatusChip status={o.status} />
                  <Typography fontSize={11} color="text.secondary" mt={0.5}>{categoryLabel(o.category)}</Typography>
                  {o.isAlcoholRelated && (
                    <Chip size="small" label={o.venueLicenceValid ? 'Licence verified' : 'Licence not verified'} color={o.venueLicenceValid ? 'success' : 'warning'} variant="outlined" sx={{ mt: 0.5 }} />
                  )}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(o.startDate)} – {fmtDate(o.endDate)}<Typography fontSize={11} color="text.secondary">{formatDays(o.activeDays)}</Typography></TableCell>
                <TableCell align="right">{o.views}</TableCell>
                <TableCell align="right">{o.claims}</TableCell>
                <TableCell align="right">{o.redeemed}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDateTime(o.updatedAt)}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {/* Every offer is approved or rejected before publication; any offer can be suspended and reinstated. */}
                  {o.moderationStatus === 'Pending' ? (
                    <>
                      <Button size="small" variant="contained" sx={{ ...goldButton, mr: 0.5 }} onClick={() => review(o, 'approve')}>Approve</Button>
                      <Button size="small" color="error" onClick={() => review(o, 'reject')}>Reject</Button>
                    </>
                  ) : o.moderationStatus === 'Rejected' ? (
                    <Button size="small" variant="contained" sx={{ ...goldButton, mr: 0.5 }} onClick={() => review(o, 'approve')}>Approve</Button>
                  ) : o.moderationStatus === 'Suspended'
                    ? <Button size="small" variant="contained" sx={{ ...goldButton, mr: 0.5 }} onClick={() => review(o, 'approve')}>Reinstate</Button>
                    : <Button size="small" color="error" onClick={() => review(o, 'suspend')}>Suspend</Button>}
                  <Button size="small" onClick={() => setHistoryOf(o)}>History</Button>
                  <Tooltip title="Edit in the venue's partner portal">
                    <IconButton size="small" onClick={() => navigate(`/partner-account/offers/${o.offerId}/edit?restoId=${o.restoId}`)} aria-label={`Edit ${o.title}`}><EditOutlinedIcon fontSize="small" /></IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Pager count={t.filtered.length} page={t.page} perPage={t.perPage} setPage={t.setPage} setPerPage={t.setPerPage} />
      <ReviewDialog target={reviewing} onClose={() => setReviewing(null)} onDone={(text) => { setReviewing(null); notify({ severity: 'success', text }); reload(); }} />
      <OfferHistoryDialog offer={historyOf} onClose={() => setHistoryOf(null)} />
    </>
  );
}

const partyMatches = (p: AdminPoolPartyRow, q: string) => `${p.venueName} ${p.title} ${p.poolType} ${p.status} ${p.moderationStatus}`.toLowerCase().includes(q);

function PoolPartiesTable({ rows, reload, notify }: { rows: AdminPoolPartyRow[]; reload: () => void; notify: Notify }) {
  const navigate = useNavigate();
  const t = useTableState(rows, partyMatches, 'updatedAt', 'desc');
  const [reviewing, setReviewing] = useState<ReviewTarget | null>(null);
  const exportCsv = () => downloadCsv('cheers2night-pool-parties.csv',
    ['ID', 'Venue ID', 'Venue', 'Party', 'Type', 'Date', 'Start', 'End', 'Capacity', 'Booked', 'Price', 'Status', 'Review', 'Review note', 'Updated'],
    t.filtered.map((p) => [p.id, p.restoId, p.venueName, p.title, p.poolType, p.eventDate, p.startTime, p.endTime, p.capacity, p.bookedGuests, p.pricePerGuest, p.status, p.moderationStatus, p.moderationNote, p.updatedAt]));
  const review = (p: AdminPoolPartyRow, action: ModerationAction) => setReviewing({
    kind: 'pool party', id: p.id, title: p.title, venueName: `${p.venueName} · ${p.poolType} · ${partyWhen(p)} · ${partyPrice(p.pricePerGuest)}`, action,
    details: [['Description', p.description], ['Age policy', p.agePolicy], ['Safety rules', p.safetyRules], ['Emergency contact', p.emergencyContact], ['Previous review note', p.moderationNote]]
  });
  return (
    <>
      <Toolbar query={t.query} setQuery={t.setQuery} placeholder="Search venue, party or status (try &quot;pending&quot;)" onExport={exportCsv} onRefresh={reload} />
      <TableContainer sx={{ border: '1px solid #2a2f38', borderRadius: 2, colorScheme: 'dark' }}>
        <Table size="small" sx={{ minWidth: 1000 }}>
          <TableHead>
            <TableRow>
              <SortHead label="Venue" field="venueName" state={t} />
              <SortHead label="Party" field="title" state={t} />
              <SortHead label="Date" field="eventDate" state={t} />
              <SortHead label="Booked" field="bookedGuests" state={t} align="right" />
              <SortHead label="Status" field="status" state={t} />
              <SortHead label="Updated" field="updatedAt" state={t} />
              <SortHead label="Review" field="moderationStatus" state={t} align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {t.pageRows.length === 0 && <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>No pool parties yet. Partners create them under Pool Parties in their portal.</TableCell></TableRow>}
            {t.pageRows.map((p) => (
              <TableRow key={p.id} hover>
                <TableCell>{p.venueName}</TableCell>
                <TableCell sx={{ minWidth: 200 }}>
                  <Typography fontWeight={700} fontSize={14}>{p.title}</Typography>
                  <Typography fontSize={12} color="text.secondary">{p.poolType} · {partyPrice(p.pricePerGuest)}</Typography>
                  {p.moderationNote && <Typography fontSize={12} color="error.light">Note: {p.moderationNote}</Typography>}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{partyWhen(p)}</TableCell>
                <TableCell align="right">{p.bookedGuests} / {p.capacity}</TableCell>
                <TableCell><StatusChip status={p.status} /></TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDateTime(p.updatedAt)}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  <ReviewButtons status={p.moderationStatus} onAction={(a) => review(p, a)} />
                  <Tooltip title="Open in the venue's partner portal">
                    <IconButton size="small" onClick={() => navigate(`/partner-account/pool-parties?restoId=${p.restoId}`)} aria-label={`Open ${p.title}`}><OpenInNewIcon fontSize="small" /></IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Pager count={t.filtered.length} page={t.page} perPage={t.perPage} setPage={t.setPage} setPerPage={t.setPerPage} />
      <ReviewDialog target={reviewing} onClose={() => setReviewing(null)} onDone={(text) => { setReviewing(null); notify({ severity: 'success', text }); reload(); }} />
    </>
  );
}

const eventMatches = (e: AdminEventRow, q: string) => `${e.venueName} ${e.title} ${e.category} ${e.status} ${e.moderationStatus}`.toLowerCase().includes(q);

// Events go live when the partner publishes them; admins suspend (or reject) ones that break the rules.
function EventsTable({ rows, reload, notify }: { rows: AdminEventRow[]; reload: () => void; notify: Notify }) {
  const navigate = useNavigate();
  const t = useTableState(rows, eventMatches, 'updatedAt', 'desc');
  const [reviewing, setReviewing] = useState<ReviewTarget | null>(null);
  const exportCsv = () => downloadCsv('cheers2night-events.csv',
    ['ID', 'Venue ID', 'Venue', 'Event', 'Category', 'Date', 'Start', 'Status', 'Review', 'Review note', 'Updated'],
    t.filtered.map((e) => [e.id, e.restoId, e.venueName, e.title, e.category, e.eventDate, e.startTime, e.status, e.moderationStatus, e.moderationNote, e.updatedAt]));
  const review = (e: AdminEventRow, action: ModerationAction) => setReviewing({
    kind: 'event', id: e.id, title: e.title, venueName: `${e.venueName} · ${e.category} · ${eventWhen(e)}`, action,
    details: [['Description', e.description], ['Previous review note', e.moderationNote]]
  });
  return (
    <>
      <Toolbar query={t.query} setQuery={t.setQuery} placeholder="Search venue, event, category or status" onExport={exportCsv} onRefresh={reload} />
      <TableContainer sx={{ border: '1px solid #2a2f38', borderRadius: 2, colorScheme: 'dark' }}>
        <Table size="small" sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow>
              <SortHead label="Venue" field="venueName" state={t} />
              <SortHead label="Event" field="title" state={t} />
              <SortHead label="Date" field="eventDate" state={t} />
              <SortHead label="Status" field="status" state={t} />
              <SortHead label="Updated" field="updatedAt" state={t} />
              <SortHead label="Review" field="moderationStatus" state={t} align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {t.pageRows.length === 0 && <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>No events yet. Partners create them under Events in their portal.</TableCell></TableRow>}
            {t.pageRows.map((e) => (
              <TableRow key={e.id} hover>
                <TableCell>{e.venueName}</TableCell>
                <TableCell sx={{ minWidth: 200 }}>
                  <Typography fontWeight={700} fontSize={14}>{e.title}</Typography>
                  <Typography fontSize={12} color="text.secondary">{e.category}</Typography>
                  {e.moderationNote && <Typography fontSize={12} color="error.light">Note: {e.moderationNote}</Typography>}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{eventWhen(e)}</TableCell>
                <TableCell><StatusChip status={e.status === 'Upcoming' || e.status === 'Today' ? 'Active' : e.status === 'Past' ? 'Expired' : e.status} /></TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDateTime(e.updatedAt)}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  <ReviewButtons status={e.moderationStatus} onAction={(a) => review(e, a)} />
                  <Tooltip title="Open in the venue's partner portal">
                    <IconButton size="small" onClick={() => navigate(`/partner-account/events?restoId=${e.restoId}`)} aria-label={`Open ${e.title}`}><OpenInNewIcon fontSize="small" /></IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Pager count={t.filtered.length} page={t.page} perPage={t.perPage} setPage={t.setPage} setPerPage={t.setPerPage} />
      <ReviewDialog target={reviewing} onClose={() => setReviewing(null)} onDone={(text) => { setReviewing(null); notify({ severity: 'success', text }); reload(); }} />
    </>
  );
}

const auditMatches = (a: AuditLogEntry, q: string) => `${a.actor} ${a.action} ${a.entityType} ${a.entityId ?? ''} ${a.details ?? ''}`.toLowerCase().includes(q);

function AuditLogTable({ rows, reload }: { rows: AuditLogEntry[]; reload: () => void }) {
  const t = useTableState(rows, auditMatches, 'id', 'desc');
  const exportCsv = () => downloadCsv('cheers2night-audit-log.csv',
    ['ID', 'Time', 'Actor type', 'Actor', 'Action', 'Entity', 'Entity ID', 'Details', 'Result'],
    t.filtered.map((a) => [a.id, a.createdAt, a.actorType, a.actor, a.action, a.entityType, a.entityId, a.details, a.succeeded ? 'ok' : 'refused']));
  return (
    <>
      <Toolbar query={t.query} setQuery={t.setQuery} placeholder="Search actor, action (e.g. offer.approve, redeem, login) or details" onExport={exportCsv} onRefresh={reload} />
      <TableContainer sx={{ border: '1px solid #2a2f38', borderRadius: 2, colorScheme: 'dark' }}>
        <Table size="small" sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow>
              <SortHead label="Time" field="id" state={t} />
              <SortHead label="Actor" field="actor" state={t} />
              <SortHead label="Action" field="action" state={t} />
              <SortHead label="Entity" field="entityType" state={t} />
              <TableCell sx={{ fontWeight: 700 }}>Details</TableCell>
              <SortHead label="Result" field="succeeded" state={t} />
            </TableRow>
          </TableHead>
          <TableBody>
            {t.pageRows.length === 0 && <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>No audit entries match.</TableCell></TableRow>}
            {t.pageRows.map((a) => (
              <TableRow key={a.id} hover>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDateTime(a.createdAt)}</TableCell>
                <TableCell><Typography fontSize={13}>{a.actor}</Typography><Typography fontSize={11} color="text.secondary">{a.actorType}</Typography></TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 13 }}>{a.action}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{a.entityType}{a.entityId ? ` #${a.entityId}` : ''}</TableCell>
                <TableCell sx={{ maxWidth: 380, fontSize: 13 }}>{a.details ?? '–'}</TableCell>
                <TableCell>
                  <Chip size="small" label={a.succeeded ? 'OK' : 'Refused'} sx={{ background: a.succeeded ? '#22c55e' : '#ef4444', color: a.succeeded ? '#000' : '#fff', fontWeight: 700 }} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Pager count={t.filtered.length} page={t.page} perPage={t.perPage} setPage={t.setPage} setPerPage={t.setPerPage} />
      <Typography fontSize={12} color="text.secondary">Shows the latest 500 entries. Entries can't be edited or deleted from the app. Codes and passwords are never recorded.</Typography>
    </>
  );
}

function ClaimsTable({ rows, reload }: { rows: AdminClaimRow[]; reload: () => void }) {
  const t = useTableState(rows, claimMatches, 'claimedAtUtc', 'desc');
  const exportCsv = () => downloadCsv('cheers2night-claims.csv',
    ['Claim ID', 'Venue ID', 'Venue', 'Offer', 'Customer mobile', 'Claimed', 'Status', 'Redeemed'],
    t.filtered.map((c) => [c.id, c.restoId, c.venueName, c.offerTitle, c.mobile, c.claimedAtUtc, c.status, c.redeemedAtUtc]));
  return (
    <>
      <Toolbar query={t.query} setQuery={t.setQuery} placeholder="Search venue, offer, mobile or status" onExport={exportCsv} onRefresh={reload} />
      <TableContainer sx={{ border: '1px solid #2a2f38', borderRadius: 2, colorScheme: 'dark' }}>
        <Table size="small" sx={{ minWidth: 800 }}>
          <TableHead>
            <TableRow>
              <SortHead label="Claimed" field="claimedAtUtc" state={t} />
              <SortHead label="Venue" field="venueName" state={t} />
              <SortHead label="Offer" field="offerTitle" state={t} />
              <SortHead label="Customer mobile" field="mobile" state={t} />
              <SortHead label="Status" field="status" state={t} />
              <SortHead label="Redeemed" field="redeemedAtUtc" state={t} />
            </TableRow>
          </TableHead>
          <TableBody>
            {t.pageRows.length === 0 && <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>No claims yet.</TableCell></TableRow>}
            {t.pageRows.map((c) => (
              <TableRow key={c.id} hover>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDateTime(c.claimedAtUtc)}</TableCell>
                <TableCell>{c.venueName}</TableCell>
                <TableCell>{c.offerTitle}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}><a href={`tel:${c.mobile}`} style={{ color: 'inherit' }}>{c.mobile}</a></TableCell>
                <TableCell><StatusChip status={c.status} /></TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDateTime(c.redeemedAtUtc)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Pager count={t.filtered.length} page={t.page} perPage={t.perPage} setPage={t.setPage} setPerPage={t.setPerPage} />
      <Typography fontSize={12} color="text.secondary">Shows the latest 500 claims.</Typography>
    </>
  );
}

const applicationMatches = (a: PartnerApplication, q: string) =>
  `${a.businessName} ${a.ownerName} ${a.mobile} ${a.email} ${a.city} ${a.businessType ?? ''} ${a.status}`.toLowerCase().includes(q);

const APPLICATION_CHIP: Record<string, string> = { Pending: 'Scheduled', Approved: 'Active', Rejected: 'Expired' };

function ApplicationsTable({ rows, reload, notify }: { rows: PartnerApplication[]; reload: () => void; notify: (n: { severity: 'success' | 'error'; text: string; password?: string; email?: string }) => void }) {
  const t = useTableState(rows, applicationMatches, 'createdAt', 'desc');
  const [approving, setApproving] = useState<PartnerApplication | null>(null);
  const [expanded, setExpanded] = useState<number | null>(null);

  const reject = async (a: PartnerApplication) => {
    if (!window.confirm(`Reject the application from ${a.businessName}?`)) return;
    try {
      await rejectPartnerApplication(a.id);
      notify({ severity: 'success', text: `Application from ${a.businessName} rejected.` });
      reload();
    } catch (err) {
      notify({ severity: 'error', text: errorText(err, 'Could not reject the application.') });
    }
  };

  const exportCsv = () => downloadCsv('cheers2night-applications.csv',
    ['ID', 'Received', 'Business', 'Type', 'Owner', 'Mobile', 'Email', 'City', 'State', 'PIN', 'About', 'Status', 'Venue ID', 'Reviewed'],
    t.filtered.map((a) => [a.id, a.createdAt, a.businessName, a.businessType, a.ownerName, a.mobile, a.email, a.city, a.state, a.pinCode, a.about, a.status, a.restoId, a.reviewedAt]));

  return (
    <>
      <Toolbar query={t.query} setQuery={t.setQuery} placeholder="Search business, owner, mobile, email, city or status" onExport={exportCsv} onRefresh={reload} />
      <TableContainer sx={{ border: '1px solid #2a2f38', borderRadius: 2, colorScheme: 'dark' }}>
        <Table size="small" sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow>
              <SortHead label="Received" field="createdAt" state={t} />
              <SortHead label="Business" field="businessName" state={t} />
              <SortHead label="Owner" field="ownerName" state={t} />
              <SortHead label="Mobile / Email" field="mobile" state={t} />
              <SortHead label="City" field="city" state={t} />
              <SortHead label="Status" field="status" state={t} />
              <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {t.pageRows.length === 0 && <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>No applications yet. They arrive from the Partner With Us page.</TableCell></TableRow>}
            {t.pageRows.map((a) => (
              <React.Fragment key={a.id}>
                <TableRow hover sx={{ cursor: a.about ? 'pointer' : undefined }} onClick={() => setExpanded(expanded === a.id ? null : a.id)} title={a.about ? 'Click to read their message' : undefined}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDateTime(a.createdAt)}</TableCell>
                  <TableCell><Typography fontWeight={700} fontSize={14}>{a.businessName}</Typography>{a.businessType && <Typography fontSize={12} color="text.secondary">{a.businessType}</Typography>}</TableCell>
                  <TableCell>{a.ownerName}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <a href={`tel:${a.mobile}`} style={{ color: 'inherit' }} onClick={(e) => e.stopPropagation()}>{a.mobile}</a>
                    <Typography fontSize={12}><a href={`mailto:${a.email}`} style={{ color: GOLD }} onClick={(e) => e.stopPropagation()}>{a.email}</a></Typography>
                  </TableCell>
                  <TableCell>{[a.city, a.state].filter(Boolean).join(', ')}{a.pinCode && <Typography fontSize={12} color="text.secondary">{a.pinCode}</Typography>}</TableCell>
                  <TableCell><Chip size="small" label={a.status} sx={{ background: STATUS_COLORS[APPLICATION_CHIP[a.status]], color: a.status === 'Rejected' ? '#fff' : '#000', fontWeight: 700 }} /></TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                    {a.status === 'Pending' ? (
                      <>
                        <Button size="small" variant="contained" sx={{ ...goldButton, mr: 1 }} onClick={() => setApproving(a)}>Approve</Button>
                        <Button size="small" color="error" onClick={() => reject(a)}>Reject</Button>
                      </>
                    ) : (
                      <Typography fontSize={12} color="text.secondary">{fmtDate(a.reviewedAt)}{a.restoId ? ` · venue #${a.restoId}` : ''}</Typography>
                    )}
                  </TableCell>
                </TableRow>
                {expanded === a.id && a.about && (
                  <TableRow><TableCell colSpan={7} sx={{ whiteSpace: 'pre-line', color: 'text.secondary', background: 'rgba(255,255,255,0.02)' }}>{a.about}</TableCell></TableRow>
                )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Pager count={t.filtered.length} page={t.page} perPage={t.perPage} setPage={t.setPage} setPerPage={t.setPerPage} />

      <PartnerDialog
        open={approving !== null}
        partner={null}
        application={approving}
        onClose={() => setApproving(null)}
        onSaved={(message, password, email) => { setApproving(null); notify({ severity: 'success', text: message, password, email }); reload(); }}
      />
    </>
  );
}

// Admin console data: partner applications (approve / reject), every partner (add / edit / delete),
// every offer and pool party (with review: approve / reject / suspend), every claim, and the audit log.
const AdminDataTables: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [partners, setPartners] = useState<AdminPartner[]>([]);
  const [offers, setOffers] = useState<AdminOfferRow[]>([]);
  const [poolParties, setPoolParties] = useState<AdminPoolPartyRow[]>([]);
  const [events, setEvents] = useState<AdminEventRow[]>([]);
  const [claims, setClaims] = useState<AdminClaimRow[]>([]);
  const [applications, setApplications] = useState<PartnerApplication[]>([]);
  const [auditLog, setAuditLog] = useState<AuditLogEntry[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ severity: 'success' | 'error'; text: string; password?: string; email?: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const reload = useCallback(() => {
    setLoadError(null);
    getAdminPartners().then(setPartners).catch(() => setLoadError('Could not load partners.'));
    getAdminOffers().then(setOffers).catch(() => setLoadError('Could not load offers.'));
    getAdminClaims().then(setClaims).catch(() => setLoadError('Could not load claims.'));
    getPartnerApplications().then(setApplications).catch(() => setLoadError('Could not load applications.'));
    getAdminPoolParties().then(setPoolParties).catch(() => setLoadError('Could not load pool parties.'));
    getAdminEvents().then(setEvents).catch(() => setLoadError('Could not load events.'));
    getAuditLogs().then(setAuditLog).catch(() => setLoadError('Could not load the audit log.'));
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const copyPassword = async (password: string) => {
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Box mb={5}>
      <Typography variant="h6" color="#e6e6e6" mb={1}>Data</Typography>
      {loadError && <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>}
      {notice && (
        <Alert severity={notice.severity} sx={{ mb: 2 }} onClose={() => { setNotice(null); setCopied(false); }}>
          {notice.text}
          {notice.password && (
            <Box mt={1}>
              Login for <b>{notice.email}</b>: password <b style={{ fontFamily: 'monospace', fontSize: 16 }}>{notice.password}</b>
              <Button size="small" startIcon={<ContentCopyIcon />} onClick={() => copyPassword(notice.password!)} sx={{ ml: 1 }}>{copied ? 'Copied' : 'Copy'}</Button>
              <br />Share it with the partner now; it won't be shown again. They can change it under Account Settings.
            </Box>
          )}
        </Alert>
      )}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, '& .Mui-selected': { color: `${GOLD} !important` }, '& .MuiTabs-indicator': { background: GOLD } }} variant="scrollable" allowScrollButtonsMobile>
        <Tab label={`Applications (${applications.filter((a) => a.status === 'Pending').length} new)`} />
        <Tab label={`Partners (${partners.length})`} />
        <Tab label={`Offers (${offers.filter((o) => o.moderationStatus === 'Pending').length} to review)`} />
        <Tab label={`Pool Parties (${poolParties.filter((p) => p.moderationStatus === 'Pending').length} to review)`} />
        <Tab label={`Events (${events.length})`} />
        <Tab label={`Claims (${claims.length})`} />
        <Tab label="Audit Log" />
      </Tabs>
      {tab === 0 && <ApplicationsTable rows={applications} reload={reload} notify={setNotice} />}
      {tab === 1 && <PartnersTable rows={partners} reload={reload} notify={setNotice} />}
      {tab === 2 && <OffersTable rows={offers} reload={reload} notify={setNotice} />}
      {tab === 3 && <PoolPartiesTable rows={poolParties} reload={reload} notify={setNotice} />}
      {tab === 4 && <EventsTable rows={events} reload={reload} notify={setNotice} />}
      {tab === 5 && <ClaimsTable rows={claims} reload={reload} />}
      {tab === 6 && <AuditLogTable rows={auditLog} reload={reload} />}
    </Box>
  );
};

export default AdminDataTables;
